import { useCallback } from 'react';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import Heading from '@zenbusiness/zui/Heading';
import Stack from '@zenbusiness/zui/Stack';
import PropTypes from 'prop-types';

import AddIcon from '@mui/icons-material/Add';

import ConfirmDeleteInstantFundingDebitCardModal from '#client/components/ConfirmDeleteInstantFundingDebitCardModal';
import { FocusGrid, ContainerGrid } from '#client/components/Grid';
import ListAccountSkeleton from '#client/components/ListAccountSkeleton';
import AccountInstant from '#client/components/MoveMoney/components/AccountInstant';
import useAstraProfileContext from '#client/hooks/useAstraProfileContext';
import useFormStep from '#client/hooks/useFormStep';
import useModalContext from '#client/hooks/useModalContext';
import useModalDisplayTracker from '#client/hooks/useModalDisplayTracker';
import useOpenAstraModal from '#client/hooks/useOpenAstraModal';
import useToastContext from '#client/hooks/useToastContext';

import { DialogContentStyled } from './DialogContent.styles';
import { Wrapper, ButtonStyled } from './WhichCardForm.styles';

import { MODAL_ACTION } from '../constants/instants';

const ListCards = ({ cards, handleSelect, handleDelete }) => (
  <Stack gap={{ xs: '8px', md: '12px' }}>
    {cards.map((card) => {
      const {
        cardHolderFirstName,
        cardHolderLastName,
        cardLastFourDigits,
        cardType,
        isValidCard,
        isCardExpired
      } = card;

      // Capitalize the first letter of the card type
      const cardTypeCapitalized = cardType.charAt(0).toUpperCase() + cardType.slice(1);
      const title = `${cardTypeCapitalized} ••${cardLastFourDigits}`;
      const subtitle = `${cardHolderFirstName} ${cardHolderLastName}`;
      const handleAccount =
        isValidCard && !isCardExpired ? () => handleSelect(card) : () => handleDelete(card);
      const pillText =
        (isCardExpired && 'Expired') || (!isValidCard && 'Type not accepted') || null;

      return (
        <AccountInstant
          buttonProps={{
            variant: isValidCard && !isCardExpired ? 'contained' : 'outlined'
          }}
          buttonText={isValidCard && !isCardExpired ? 'Select' : 'Delete'}
          key={title}
          nameIcon={subtitle}
          onClick={handleAccount}
          pillText={pillText}
          subtitle={subtitle}
          title={title}
        />
      );
    })}
  </Stack>
);

ListCards.propTypes = {
  cards: PropTypes.arrayOf(
    PropTypes.shape({
      uuid: PropTypes.string,
      cardHolderFirstName: PropTypes.string,
      cardHolderLastName: PropTypes.string,
      cardStatus: PropTypes.string,
      cardType: PropTypes.string
    })
  ),
  handleSelect: PropTypes.func,
  handleDelete: PropTypes.func
};

export default function WhichCardForm({ handleClose }) {
  const { clearAllToast } = useToastContext();
  const { openModal } = useModalContext();
  const { dispatchDepositData } = useFormStep();
  const { loading, cardList, refreshAstraProfile } = useAstraProfileContext();
  const { getAndRedirectAstraUrl } = useOpenAstraModal();

  const isNotFullCardList = cardList.length < 3;

  const buttonSelectPressed = useButtonPressedCallback({
    elementName: 'select_dc_astra',
    elementText: 'Select',
    appPageName: 'astra_choose_card_modal'
  });

  const buttonAddNewPressed = useButtonPressedCallback({
    elementName: 'add_new_dc_astra',
    elementText: 'Add new (3 max)',
    appPageName: 'astra_choose_card_modal'
  });

  const handleSelect = useCallback(
    (account) => {
      buttonSelectPressed();
      clearAllToast();

      dispatchDepositData({
        type: MODAL_ACTION.GO_TO_HOW_MUCH_FORM,
        payload: {
          selectedFromAccount: account
        }
      });
    },
    [buttonSelectPressed, clearAllToast, dispatchDepositData]
  );

  const handleDelete = useCallback(
    (account) => {
      clearAllToast();

      openModal({
        component: ConfirmDeleteInstantFundingDebitCardModal,
        id: account.uuid,
        handleSuccess: refreshAstraProfile
      });
    },
    [clearAllToast, openModal, refreshAstraProfile]
  );

  const handleAddNew = () => {
    buttonAddNewPressed();
    getAndRedirectAstraUrl();
  };

  useModalDisplayTracker({
    elementName: 'astra_choose_card_modal',
    elementText: 'astra_choose_card_modal',
    appPageName: 'astra_choose_card_modal'
  });

  return (
    <DialogContentStyled role="form">
      <ContainerGrid>
        <FocusGrid>
          <Wrapper>
            <Stack>
              <Heading
                align="center"
                size="3xl"
                sx={{ marginBottom: '40px' }}
                weight="bold"
              >
                Which card do you want to use?
              </Heading>
              <Stack
                direction="row"
                justifyContent="right"
              >
                <ButtonStyled
                  disabled={!isNotFullCardList}
                  icon={<AddIcon />}
                  onClick={handleAddNew}
                  size="medium"
                  sx={{ marginBottom: '24px' }}
                  variant="text"
                >
                  Add new (3 max)
                </ButtonStyled>
              </Stack>
              {loading ? (
                <ListAccountSkeleton />
              ) : (
                <ListCards
                  cards={cardList}
                  handleClose={handleClose}
                  handleDelete={handleDelete}
                  handleSelect={handleSelect}
                />
              )}
            </Stack>
          </Wrapper>
        </FocusGrid>
      </ContainerGrid>
    </DialogContentStyled>
  );
}

WhichCardForm.propTypes = {
  handleClose: PropTypes.func,
  actionsKit: PropTypes.shape({
    resetSearchParameters: PropTypes.func
  })
};
